import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaHome } from 'react-icons/fa';
import { RiArrowRightSLine, RiArrowLeftSLine } from "react-icons/ri";
import './Breadcrumbs.css';
import { useAppLanguage } from '../../../Hooks/useAppLanguage';
import { stripLangPrefix } from '../../../Utils/langPath';

const routes = [
    { path: '/', breadcrumbKey: 'breadcrumb.home', icon: FaHome },
    { path: '/products', breadcrumbKey: 'breadcrumb.products' },
    { path: '/product-details/:id', breadcrumbKey: 'breadcrumb.productDetails' },
    { path: '/about', breadcrumbKey: 'breadcrumb.about' },
    { path: '/contact', breadcrumbKey: 'breadcrumb.contact' },
    { path: '/faq', breadcrumbKey: 'breadcrumb.faq' },
    { path: '/testimonials', breadcrumbKey: 'breadcrumb.testimonials' },
    { path: '/gallery', breadcrumbKey: 'breadcrumb.gallery' },
    { path: '/cart', breadcrumbKey: 'breadcrumb.cart' },
    { path: '/wishlist', breadcrumbKey: 'breadcrumb.wishlist' },
    { path: '/track-order', breadcrumbKey: 'breadcrumb.trackOrder' },
    { path: '/my-orders', breadcrumbKey: 'breadcrumb.myOrders' },
    { path: '/profile', breadcrumbKey: 'breadcrumb.profile' },
];

const matchRoute = (pathname) => {
    for (const route of routes) {
        const routePath = route.path.replace(/:\w+/g, '[^/]+');
        const match = new RegExp(`^${routePath}$`).test(pathname);
        if (match) return route;
    }
    return null;
};

const Breadcrumbs = ({ onBreadcrumbClick }) => {
    const { t, dir, isRtl, langPath } = useAppLanguage();
    const [breadcrumbData, setBreadcrumbData] = useState({});
    const [currentHeading, setCurrentHeading] = useState(t('breadcrumb.home'));
    const [loading, setLoading] = useState(true);

    const location = useLocation();
    const pathWithoutLang = stripLangPrefix(location.pathname);

    useEffect(() => {
        setBreadcrumbData({});
        setLoading(false);
    }, []);

    useEffect(() => {
        if (pathWithoutLang) {
            const pathnames = pathWithoutLang.split('/').filter(x => x);
            if (pathnames.length > 0) {
                const lastPath = `/${pathnames.join('/')}`;
                const route = matchRoute(lastPath);
                const heading = breadcrumbData[lastPath] || (route && t(route.breadcrumbKey));
                setCurrentHeading(heading || t('breadcrumb.home'));
            } else {
                setCurrentHeading(t('breadcrumb.home'));
            }
        }
    }, [location, breadcrumbData, t, pathWithoutLang]);

    const excludedRoutes = ['/sign-in', '/sign-up', '/reset-password', '/forgot-password', '/login', '/register', '/profile'];
    if (loading || excludedRoutes.includes(pathWithoutLang)) return null;

    if (pathWithoutLang === '/') return null;

    const pathnames = pathWithoutLang.split('/').filter(x => x);
    const breadcrumbItems = pathnames.map((_, index) => {
        const to = `/${pathnames.slice(0, index + 1).join('/')}`;
        const route = matchRoute(to);
        const breadcrumbLabel = breadcrumbData[to] || (route && t(route.breadcrumbKey));

        return breadcrumbLabel ? (
            <span key={to}>
                <Link
                    to={langPath(to)}
                    onClick={() => onBreadcrumbClick && onBreadcrumbClick(to)}
                    className="breadcrumbs-link"
                    title={breadcrumbLabel}
                >
                    {route && route.icon && <route.icon className="breadcrumb-icon" />}
                    {breadcrumbLabel}
                </Link>
            </span>
        ) : null;
    });

    return (
        <div className="breadcrumbs">
            <h2 className="breadcrumbs-title">{currentHeading}</h2>
            <div className='breadcrumbs-content' dir={dir}>
                <Link to={langPath('/')}>
                    <FaHome className="breadcrumb-icon breadcrumb-home-icon" />
                </Link>
                {isRtl ?
                    <RiArrowLeftSLine className='breadcrumb-icon' />
                    :
                    <RiArrowRightSLine className='breadcrumb-icon' />
                }
                {breadcrumbItems.length > 0 ? breadcrumbItems : t('breadcrumb.home')}
            </div>
        </div>
    );
};

export default Breadcrumbs;
